import {ArrowLeft, ExternalLink} from '@nlabs/gothamjs/icons';
import {Link, useLoaderData} from '@nlabs/gothamjs/router';
import {useEffect} from 'react';

import {SiteFooter} from '../components/SiteFooter';
import {SiteHeader} from '../components/SiteHeader';
import {findSectionByLabel, sectionPath} from '../data/sections';
import type {ArticleRouteData} from '../routes/articleLoader';

const formatArticleDate = (value: string) => new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeStyle: 'short',
  timeZone: 'America/Chicago'
}).format(new Date(value));

export const ArticlePage = () => {
  const {article} = useLoaderData() as ArticleRouteData;
  const articleSection = findSectionByLabel(article.section);
  const isExternal = article.sourceType === 'external';

  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = `${article.headline} | Chicago Sentinel Times`;
    return () => { document.title = previousTitle; };
  }, [article.headline, article.id]);

  return (
    <div className="newspaper">
      <SiteHeader />
      <main className="article-page" id="main-content">
        <article>
          <header className="shell article-header">
            <nav aria-label="Breadcrumb" className="article-breadcrumb">
              <Link to="/"><ArrowLeft aria-hidden="true" size={14} /> Home</Link>
              <span aria-hidden="true">/</span>
              {articleSection ? <Link to={sectionPath(articleSection)}>{article.section}</Link> : <span>{article.section}</span>}
            </nav>
            {article.isFixture ? <p className="fixture-notice"><strong>Development fixture</strong> — This is non-live demonstration content.</p> : null}
            {article.disclosure ? <p className="editorial-disclosure"><strong>Editorial disclosure</strong> — {article.disclosure}</p> : null}
            {isExternal ? (
              <p className="external-story-notice">
                <strong>External report</strong> — Published by {article.sourceName}. The complete article remains on the publisher’s site.
              </p>
            ) : null}
            <span className="story-card__section">{article.section}</span>
            <h1>{article.headline}</h1>
            <p className="article-deck">{article.description}</p>
            <div className="article-byline">
              <div>
                <strong>{article.author?.name ?? article.sourceName}</strong>
                {article.author?.role ? <span>{article.author.role}</span> : null}
              </div>
              <time dateTime={article.publishedAt}>Published {formatArticleDate(article.publishedAt)}</time>
            </div>
          </header>

          {article.image ? (
            <figure className="shell article-hero">
              <img alt={article.image.alt} src={article.image.url} decoding="async" />
              <figcaption>{article.image.credit}</figcaption>
            </figure>
          ) : null}

          <div className="shell article-layout">
            <div className="article-body">
              {isExternal ? (
                <>
                  {article.excerpt ? <p className="external-story-excerpt">{article.excerpt}</p> : null}
                  <div className="external-story-handoff">
                    <p>Continue to {article.sourceName} to read the complete article and view any updates from the original publisher.</p>
                    <a href={article.canonicalUrl} rel="noreferrer" target="_blank">
                      Read the full article at {article.sourceName}<ExternalLink aria-hidden="true" size={16} />
                    </a>
                  </div>
                </>
              ) : article.body?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            <aside className="article-source" aria-label="Article information">
              <h2>Article information</h2>
              <dl>
                <div><dt>Published by</dt><dd>{article.sourceName}</dd></div>
                <div><dt>Section</dt><dd>{article.section}</dd></div>
                <div><dt>Topics</dt><dd>{article.tags.join(', ')}</dd></div>
              </dl>
              {isExternal ? (
                <a href={article.canonicalUrl} rel="noreferrer" target="_blank">
                  Original article<ExternalLink aria-hidden="true" size={13} />
                </a>
              ) : null}
              {article.references?.map((reference) => (
                <a href={reference.url} key={reference.url} rel="noreferrer" target="_blank">
                  {reference.label}<ExternalLink aria-hidden="true" size={13} />
                </a>
              ))}
            </aside>
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
};
